import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Truck, Scissors, Sparkles, X, ChevronLeft, ChevronRight, ZoomIn, Bell, CalendarPlus, Info } from "lucide-react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import api, { mediaUrl } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useCurrency } from "@/context/CurrencyContext";
import ProductCard from "@/components/storefront/ProductCard";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSEO } from "@/lib/seo";
import { trackEvent } from "@/components/storefront/ConsentBanner";

// Editorial image-sequencing — a studio set becomes a paced spread.
// Garment pixels are never altered; only rhythm, scale and alignment carry the story.
const SEQ = [
  { w: "w-full", aspect: "aspect-[3/4]", align: "mx-auto", parallax: [0, -30] },
  { w: "md:w-[78%]", aspect: "aspect-[4/5]", align: "md:ml-auto", parallax: [0, -50] },
  { w: "md:w-[68%]", aspect: "aspect-square", align: "md:mr-auto", parallax: [0, -20] },
  { w: "w-full", aspect: "aspect-[4/5]", align: "mx-auto", parallax: [0, -40] },
];

const EditorialImage = ({ src, alt, index, onOpen }) => {
  const ref = useRef(null);
  const s = SEQ[index % SEQ.length];
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], s.parallax);
  return (
    <div ref={ref} className={`relative ${s.w} ${s.align}`}>
      <motion.div style={{ y }} onClick={onOpen} className={`group relative w-full bg-sand cursor-zoom-in`}>
        {/* Full uploaded picture, natural height — never cropped on any device. */}
        <img src={mediaUrl(src)} alt={alt} loading="lazy" className="block w-full h-auto" />
        <span className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-espresso/70 bg-ivory/80 backdrop-blur-sm p-2">
          <ZoomIn size={16} strokeWidth={1.4} />
        </span>
      </motion.div>
    </div>
  );
};

// Full-screen garment viewer — click to zoom (origin follows cursor), arrows / Esc to navigate.
const Lightbox = ({ images, index, onClose, onNav }) => {
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("center");
  useEffect(() => { setZoomed(false); }, [index]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, onNav]);
  const move = (e) => {
    if (!zoomed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: "easeInOut" }}
      className="fixed inset-0 z-[120] bg-cocoa/[0.97] flex items-center justify-center" data-testid="pdp-lightbox">
      <button onClick={onClose} data-testid="lightbox-close" aria-label="Close"
        className="absolute top-5 right-5 md:top-8 md:right-10 text-cream/80 hover:text-cream transition-colors z-10"><X strokeWidth={1.4} size={26} /></button>
      {images.length > 1 && (
        <>
          <button onClick={() => onNav(-1)} data-testid="lightbox-prev" aria-label="Previous"
            className="absolute left-3 md:left-8 text-cream/60 hover:text-cream transition-colors z-10"><ChevronLeft strokeWidth={1.1} size={40} /></button>
          <button onClick={() => onNav(1)} data-testid="lightbox-next" aria-label="Next"
            className="absolute right-3 md:right-8 text-cream/60 hover:text-cream transition-colors z-10"><ChevronRight strokeWidth={1.1} size={40} /></button>
        </>
      )}
      <div className="w-full h-full flex items-center justify-center p-8 md:p-16 overflow-hidden" onMouseMove={move} onClick={() => setZoomed((z) => !z)}>
        <img src={mediaUrl(images[index])} alt="" style={{ transformOrigin: origin }}
          className={`max-w-full max-h-full object-contain transition-transform duration-300 ${zoomed ? "scale-[2.2] cursor-zoom-out" : "cursor-zoom-in"}`} />
      </div>
      <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-cream/50 text-[0.62rem] tracking-[0.24em] uppercase">{index + 1} / {images.length}</p>
    </motion.div>
  );
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const Rail = ({ overline, title, items, testid }) => {
  if (!items?.length) return null;
  return (
    <section className="max-w-[1700px] mx-auto px-5 md:px-10 py-20 md:py-32 border-t border-espresso/10" data-testid={testid}>
      <Reveal>
        {overline && <p className="overline text-gold mb-3">{overline}</p>}
        <h2 className="font-serif text-3xl md:text-5xl tracking-tight mb-12 md:mb-16">{title}</h2>
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-12">
        {items.slice(0, 4).map((r, i) => (
          <Reveal key={r.slug} delay={i * 0.08}><ProductCard product={r} /></Reveal>
        ))}
      </div>
    </section>
  );
};

const pad = (n) => String(n).padStart(2, "0");
const CDUnit = ({ v, l }) => (
  <div className="flex flex-col items-center">
    <span className="font-serif text-3xl md:text-4xl tabular-nums leading-none">{pad(v)}</span>
    <span className="mt-1.5 text-[0.56rem] tracking-[0.24em] uppercase text-espresso/45">{l}</span>
  </div>
);

// Per-product "coming soon" — a live countdown to the drop plus a piece-specific waitlist.
const ComingSoonPanel = ({ dropAt, slug, colour, name }) => {
  const target = dropAt ? new Date(dropAt).getTime() : null;
  const [left, setLeft] = useState(target ? target - Date.now() : 0);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sub, setSub] = useState(false);
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setLeft(target - Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  const live = target && left <= 0;
  const dropWhen = target ? new Date(target).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : null;
  const _cal = (ms) => new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
  const gcalUrl = target ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`${name || "A new piece"} — Drop`)}&dates=${_cal(target)}/${_cal(target + 3600000)}&details=${encodeURIComponent("Drops on NoirbyAmara")}` : "#";
  const addIcs = () => {
    if (!target) return;
    const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//NoirbyAmara//Drop//EN", "BEGIN:VEVENT", `UID:${slug}-drop@noirbyamara`, `DTSTAMP:${_cal(Date.now())}`, `DTSTART:${_cal(target)}`, `DTEND:${_cal(target + 3600000)}`, `SUMMARY:${name || "A new piece"} — Drop`, "DESCRIPTION:Drops on NoirbyAmara", "END:VEVENT", "END:VCALENDAR"].join("\r\n");
    const u = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
    const a = document.createElement("a"); a.href = u; a.download = `${slug}-drop.ics`; a.click();
  };
  const d = Math.max(0, Math.floor(left / 86400000));
  const h = Math.max(0, Math.floor((left % 86400000) / 3600000));
  const m = Math.max(0, Math.floor((left % 3600000) / 60000));
  const s = Math.max(0, Math.floor((left % 60000) / 1000));
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/waitlist", { email, product_slug: slug, colour: colour || null, size: null, subscribe: sub });
      setDone(true);
      toast.success("You're on the list — we'll let you know the moment it drops.");
    } catch { toast.error("Something went wrong. Please try again."); }
    setBusy(false);
  };
  return (
    <div className="mt-10 border-t border-espresso/10 pt-8" data-testid="pdp-coming-soon">
      <p className="overline text-gold mb-5">{live ? "Now available" : "Arriving soon"}</p>
      {target && !live && (
        <div className="mb-6" data-testid="pdp-countdown">
          <div className="flex gap-6 md:gap-8">
            <CDUnit v={d} l="Days" /><CDUnit v={h} l="Hours" /><CDUnit v={m} l="Minutes" /><CDUnit v={s} l="Seconds" />
          </div>
          {dropWhen && <p className="mt-4 text-[0.68rem] tracking-[0.18em] uppercase text-espresso/50" data-testid="pdp-drop-when">Drops {dropWhen}</p>}
        </div>
      )}
      {!target && <p className="text-espresso/65 font-light leading-relaxed mb-6">This piece is being prepared. Join the list to be first to know when it arrives.</p>}

      {done ? (
        <p className="text-sage text-sm" data-testid="pdp-notify-done">Thank you — you'll be the first to know.</p>
      ) : (
        <div className="bg-sand/50 border border-espresso/10 p-5">
          <p className="font-serif text-xl leading-snug mb-1">Join the waitlist</p>
          <p className="text-xs text-espresso/60 mb-4">Be first to shop it the moment it drops.</p>
          <form onSubmit={submit} className="flex flex-col gap-3" data-testid="pdp-notify-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address" data-testid="pdp-notify-email"
              className="w-full bg-ivory border border-espresso/25 px-4 py-3.5 text-sm outline-none focus:border-espresso" />
            <label className="flex items-start gap-2 text-xs text-espresso/60 cursor-pointer select-none" data-testid="pdp-notify-optin">
              <input type="checkbox" checked={sub} onChange={(e) => setSub(e.target.checked)} className="mt-0.5 accent-espresso" />
              Also email me news &amp; new drops from NoirbyAmara.
            </label>
            <button type="submit" disabled={busy} data-testid="pdp-notify-submit"
              className="flex items-center justify-center gap-2 bg-espresso text-cream px-6 py-3.5 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-cocoa transition-colors disabled:opacity-50">
              <Bell size={13} strokeWidth={1.6} /> Notify me when it drops
            </button>
          </form>
        </div>
      )}

      {target && !live && (
        <div className="mt-5">
          <p className="text-[0.6rem] tracking-[0.2em] uppercase text-espresso/45 mb-2.5">Add the drop to your calendar</p>
          <div className="flex flex-wrap gap-2.5">
            <a href={gcalUrl} target="_blank" rel="noopener noreferrer" data-testid="drop-gcal"
              className="flex items-center gap-2 border border-espresso/30 px-4 py-2.5 text-[0.62rem] tracking-[0.16em] uppercase hover:bg-espresso hover:text-cream transition-colors">
              <CalendarPlus size={13} strokeWidth={1.6} /> Google
            </a>
            <button onClick={addIcs} data-testid="drop-ics"
              className="flex items-center gap-2 border border-espresso/30 px-4 py-2.5 text-[0.62rem] tracking-[0.16em] uppercase hover:bg-espresso hover:text-cream transition-colors">
              <CalendarPlus size={13} strokeWidth={1.6} /> Apple / Outlook
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProductPage = () => {
  const { slug } = useParams();
  const { addToBag, saved, toggleSaved } = useStore();
  const { format, isConverted } = useCurrency();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [colourIdx, setColourIdx] = useState(0);
  const [size, setSize] = useState(null);
  const [look, setLook] = useState([]);
  const [deptLook, setDeptLook] = useState([]);
  const [readMore, setReadMore] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (i) => setLightbox(i);

  const heroRef = useRef(null);
  const { scrollYProgress: heroProg } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroScale = useTransform(heroProg, [0, 1], [1, 1.08]);
  const heroFade = useTransform(heroProg, [0, 0.85], [1, 0]);
  const heroBlurY = useTransform(heroProg, [0, 1], [0, 80]);

  useEffect(() => {
    setData(null); setNotFound(false); setColourIdx(0); setSize(null);
    setLook([]); setDeptLook([]); setReadMore(false); setLightbox(null);
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      const p = r.data.product || {};
      const col = p.collection_slug;
      const dept = p.department;
      if (col) api.get(`/products?collection=${col}`).then((res) =>
        setLook(res.data.filter((x) => x.slug !== slug))).catch(() => {});
      if (dept) api.get(`/products?department=${dept}`).then((res) =>
        setDeptLook(res.data.filter((x) => x.slug !== slug))).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  const sp = data?.product;
  const line = sp && sp.department === "men" ? "ORI by Nior Amara" : "Nior Amara";
  useSEO(sp ? {
    title: sp.seo?.title || `${sp.name} | ${line}`,
    description: sp.seo?.description || sp.short_description,
    image: sp.seo?.og_image || sp.colors?.[0]?.main_image,
    jsonLd: {
      "@context": "https://schema.org", "@type": "Product", name: sp.name,
      description: sp.short_description, image: (sp.colors?.[0]?.images || []).map(mediaUrl),
      brand: { "@type": "Brand", name: line }, category: sp.category,
      ...(sp.price != null ? { offers: { "@type": "Offer", priceCurrency: "GBP", price: sp.price,
        availability: "https://schema.org/InStock" } } : {}),
    },
  } : {});

  if (notFound) return <div className="min-h-[60vh] flex items-center justify-center font-serif text-3xl">She's not here.</div>;
  if (!data) return <div className="min-h-[70vh]" />;

  const p = data.product;
  const colour = p.colors?.[colourIdx] || {};
  const allImgs = ((colour.images && colour.images.length) ? colour.images : [colour.main_image]).filter(Boolean);
  const hero = colour.lifestyle_image || allImgs[0];
  const galleryImgs = [hero, ...allImgs.filter((g) => g && g !== hero)];
  const rest = galleryImgs.slice(1);
  const sizeObj = colour.sizes?.find((s) => s.size === size);
  const allSoldOut = colour.sizes?.length > 0 && colour.sizes.every((s) => s.stock <= 0);
  const soldOut = (sizeObj && sizeObj.stock <= 0) || allSoldOut;
  const isSaved = saved.includes(p.slug);

  const related = (data.related || []).filter((x) => x.slug !== p.slug);
  const collectionRest = look;
  const isComingSoon = p.status === "coming_soon" || (p.drop_at && new Date(p.drop_at).getTime() > Date.now());
  const hasScene = !!p.editorial_image;
  // Department rail excludes the current piece and anything already shown in the full-look rail.
  const collectionSlugs = new Set(collectionRest.map((x) => x.slug));
  const deptRail = deptLook.filter((x) => !collectionSlugs.has(x.slug));
  const deptLabel = p.department === "men" ? "The Men's Atelier" : "The Women's Atelier";
  const collectionName = (p.collection_slug || "").toUpperCase();

  const handleAdd = () => {
    if (allSoldOut) { toast("This piece is sold out."); return; }
    if (!size) { toast("Please select a size."); return; }
    if (soldOut) { toast("This piece is sold out."); return; }
    addToBag({ productId: p.id, slug: p.slug, name: p.name, colour: colour.name, size, price: p.price, image: colour.main_image, qty: 1 });
    trackEvent("add_to_cart", { currency: "GBP", value: p.price || 0, items: [{ item_name: p.name, item_variant: `${colour.name}/${size}` }] });
    toast.success(`${p.name} added to your selection.`);
  };

  return (
    <div className="bg-ivory">
      {/* 1 — Breadcrumb */}
      <div className="max-w-[1700px] mx-auto px-5 md:px-10 pt-8 md:pt-12">
        <nav className="text-[0.66rem] tracking-[0.18em] uppercase text-espresso/45" data-testid="pdp-breadcrumb">
          <Link to="/" className="hover-underline">Home</Link> <span className="mx-1.5">/</span>
          <Link to={`/collections/${p.collection_slug || p.department}`} className="hover-underline">{p.category}</Link> <span className="mx-1.5">/</span>
          <span className="text-espresso/70">{p.name}</span>
        </nav>
      </div>

      {/* 2 — Oversized hero product visual */}
      {hero ? (
        <div ref={heroRef} className="relative w-full mt-6 md:mt-8 overflow-hidden" data-testid="pdp-hero"
          style={{ backgroundColor: colour.swatch || "#241c17" }}>
          {/* Premium frame: a soft blurred wash of the photo over the piece's own colour tone. */}
          <motion.div aria-hidden style={{ y: heroBlurY, backgroundImage: `url(${mediaUrl(hero)})` }}
            className="absolute inset-0 bg-center bg-cover scale-125 blur-2xl opacity-60" />
          <div aria-hidden className="absolute inset-0 bg-black/25" />
          <div onClick={() => openLightbox(0)} className="group relative w-full flex justify-center cursor-zoom-in">
            <div className="relative">
              {/* Whole picture always — never cropped, same single upload on mobile & desktop. */}
              <img src={mediaUrl(hero)} alt={p.name}
                className="block w-auto max-w-full max-h-[90vh] object-contain shadow-2xl" />
              <span className="absolute top-5 right-5 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-espresso/70 bg-ivory/80 backdrop-blur-sm p-2">
                <ZoomIn size={18} strokeWidth={1.4} />
              </span>
              <motion.div style={{ opacity: heroFade }} className="absolute inset-x-0 bottom-0 p-6 md:p-12 bg-gradient-to-t from-espresso/45 via-espresso/10 to-transparent pointer-events-none">
                {p.badges?.[0] && <p className="text-cream/90 text-[0.58rem] tracking-[0.24em] uppercase mb-2.5">{p.badges.join(" · ")}</p>}
                <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl text-cream leading-[0.98] tracking-tight">{p.name}</h1>
              </motion.div>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-[1700px] mx-auto px-5 md:px-10 mt-6">
          <div className="w-full aspect-[16/10] flex items-center justify-center bg-sand/50" data-testid="pdp-empty">
            <p className="font-serif italic text-2xl text-espresso/50">Imagery arriving soon.</p>
          </div>
        </div>
      )}

      {/* 3 — Split gallery + sticky buy panel */}
      <div className="max-w-[1700px] mx-auto px-5 md:px-10 pb-8 pt-12 md:pt-24 md:grid md:grid-cols-12 md:gap-10 lg:gap-16">
        {hasScene && (
          <div className="md:col-span-3 md:sticky md:top-28 md:self-start order-3 md:order-1 mb-12 md:mb-0" data-testid="pdp-editorial">
            <div className="w-full aspect-[3/4] overflow-hidden bg-sand">
              <img src={mediaUrl(p.editorial_image)} alt={`${p.name} — editorial`} className="w-full h-full object-contain" />
            </div>
            <p className="mt-3 text-[0.6rem] tracking-[0.22em] uppercase text-espresso/40">In the world of {p.name}</p>
          </div>
        )}
        {/* Gallery — remaining stills, paced */}
        <div className={`${hasScene ? "md:col-span-5" : "md:col-span-8"} order-2 md:order-2`}>
          {rest.length > 0 ? (
            <motion.div key={`${colour.name}-${colourIdx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, ease: "easeInOut" }}
              className="space-y-16 md:space-y-28" data-testid="pdp-gallery">
              {rest.map((g, i) => <EditorialImage key={`${colour.name}-${i}`} src={g} alt={`${p.name}${colour.name ? ` — ${colour.name}` : ""}`} index={i} onOpen={() => openLightbox(i + 1)} />)}
            </motion.div>
          ) : hero ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, ease: "easeInOut" }} data-testid="pdp-gallery">
              <EditorialImage src={hero} alt={`${p.name}${colour.name ? ` — ${colour.name}` : ""}`} index={0} onOpen={() => openLightbox(0)} />
            </motion.div>
          ) : null}
        </div>

        {/* Buy panel — sticky, calm, considered */}
        <aside className="md:col-span-4 md:sticky md:top-28 md:self-start order-1 md:order-3 mb-16 md:mb-0">
          {p.badges?.[0] && <p className="overline text-gold mb-4">{p.badges.join(" · ")}</p>}
          <p className="text-[0.62rem] tracking-[0.22em] uppercase text-espresso/45 mb-3">{line}</p>
          <div className="flex justify-between items-start gap-4">
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.02] tracking-tight">{p.name}</h2>
            <button onClick={() => toggleSaved(p.slug)} data-testid={TID.saveToggle} aria-label="Save" className="mt-1 shrink-0">
              <Heart strokeWidth={1.3} className={isSaved ? "fill-wine text-wine" : "text-espresso/70"} />
            </button>
          </div>
          {p.drop_slug && <p className="text-[0.68rem] tracking-[0.18em] uppercase text-espresso/45 mt-3">{p.drop_slug.replace("-", " ").toUpperCase()}</p>}
          <div className="flex items-center gap-2 mt-5">
            <p className="text-xl font-light" data-testid="pdp-price">{format(p.price)}</p>
            {isConverted && p.price != null && (
              <span data-testid="pdp-price-note" title="Approximate — you'll be charged the equivalent in GBP at checkout." className="text-espresso/40 hover:text-espresso/70 transition-colors cursor-help">
                <Info size={14} strokeWidth={1.6} />
              </span>
            )}
          </div>

          {p.short_description && (
            <div className="mt-6 max-w-md">
              <p className="text-espresso/70 font-light leading-relaxed">{p.short_description}</p>
              {p.long_description && (
                <>
                  <motion.div initial={false} animate={{ height: readMore ? "auto" : 0, opacity: readMore ? 1 : 0 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }} className="overflow-hidden">
                    <p className="text-espresso/70 font-light leading-relaxed mt-4 whitespace-pre-line">{p.long_description}</p>
                  </motion.div>
                  <button onClick={() => setReadMore((v) => !v)} data-testid="pdp-read-more"
                    className="mt-3 text-[0.66rem] tracking-[0.16em] uppercase hover-underline text-espresso/60">
                    {readMore ? "Read less" : "Read more"}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Colours */}
          {p.colors?.length > 0 && (
            <div className="mt-10">
              <p className="overline text-espresso/55 mb-4">Colour — <span className="text-espresso normal-case tracking-normal font-serif text-base">{colour.name}</span></p>
              <div className="flex flex-wrap gap-3">
                {p.colors.map((c, i) => (
                  <button key={c.name} onClick={() => { setColourIdx(i); setSize(null); }}
                    data-testid={TID.colourSwatch(c.name.replace(/\W+/g, "-").toLowerCase())} title={c.name}
                    className={`w-9 h-9 rounded-full border transition-[border-color,transform] duration-500 ${colourIdx === i ? "border-espresso scale-110" : "border-espresso/20 hover:border-espresso/50"}`}
                    style={{ background: c.swatch }} />
                ))}
              </div>
            </div>
          )}

          {isComingSoon ? (
            <ComingSoonPanel dropAt={p.drop_at} slug={p.slug} colour={colour.name} name={p.name} />
          ) : (
          <>
          {/* Sizes */}
          {colour.sizes?.length > 0 && (
            <div className="mt-10">
              <div className="flex justify-between mb-3">
                <p className="overline text-espresso/55">Size</p>
                <Link to="/pages/size-guide" className="text-[0.66rem] tracking-[0.14em] uppercase hover-underline text-espresso/55">Size Guide</Link>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {colour.sizes.map((s) => {
                  const out = s.stock <= 0;
                  return (
                    <button key={s.size} onClick={() => setSize(s.size)} disabled={out}
                      data-testid={TID.sizeOption(s.size)}
                      className={`min-w-[54px] px-4 py-3 text-sm border transition-colors duration-300 ${size === s.size ? "bg-espresso text-cream border-espresso" : "border-espresso/25 hover:border-espresso"} ${out ? "opacity-40 line-through cursor-not-allowed" : ""}`}>
                      {s.size}
                    </button>
                  );
                })}
              </div>
              {sizeObj && sizeObj.stock > 0 && sizeObj.stock <= (sizeObj.low_stock_threshold || 3) && (
                <p className="text-xs text-wine mt-2">Low stock — {sizeObj.stock} remaining</p>
              )}
            </div>
          )}

          {p.fit_note && (
            <div className="mt-8 flex items-start gap-2 border-l-2 border-gold/50 pl-3">
              <p className="text-xs text-espresso/70 tracking-wide leading-relaxed" data-testid="product-fit-note-display">{p.fit_note}</p>
              <Link to="/pages/size-guide" className="shrink-0 text-[0.62rem] tracking-[0.14em] uppercase hover-underline text-espresso/50 mt-0.5">Size Guide</Link>
            </div>
          )}

          <div className="mt-10 flex flex-col gap-3">
            <button onClick={handleAdd} data-testid={TID.addToBag} disabled={soldOut}
              className="w-full bg-espresso text-cream py-5 text-[0.72rem] tracking-[0.24em] uppercase hover:bg-cocoa transition-colors active:scale-[0.98] disabled:opacity-50">
              {soldOut ? "Sold Out" : "Add to Bag"}
            </button>
            {soldOut && (
              <button onClick={() => { const email = prompt("Enter your email to be notified"); if (email) api.post("/waitlist", { email, product_slug: p.slug, colour: colour.name, size }).then(() => toast.success("We'll let you know.")); }}
                className="w-full border border-espresso py-4 text-[0.72rem] tracking-[0.2em] uppercase hover:bg-espresso hover:text-cream transition-colors">Notify Me</button>
            )}
          </div>
          </>
          )}

          <Accordion type="single" collapsible className="mt-12 border-t border-espresso/10">
            {[["The Details", p.details], ["Fit & Sizing", p.fit], ["Fabric & Care", `${p.composition || ""}\n${p.care || ""}`.trim()], ["The Story", p.long_description], ["Delivery & Returns", "See our Delivery and Returns pages for full details."]]
              .filter(([, v]) => v).map(([label, val]) => (
                <AccordionItem key={label} value={label} className="border-b border-espresso/10">
                  <AccordionTrigger className="font-sans text-[0.78rem] tracking-[0.14em] uppercase py-4 hover:no-underline">{label}</AccordionTrigger>
                  <AccordionContent className="text-espresso/70 font-light leading-relaxed whitespace-pre-line pb-5">{val}</AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </aside>
      </div>

      {/* 5 — Our commitment */}
      <section className="bg-cocoa text-cream mt-20 md:mt-32" data-testid="pdp-commitment">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36 text-center">
          <Reveal>
            <p className="text-[0.62rem] tracking-[0.28em] uppercase text-gold mb-6">Our Commitment</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight max-w-3xl mx-auto">
              Each piece is made slowly, in small numbers, by hands that know the craft.
            </h2>
            <p className="text-cream/70 font-light leading-relaxed max-w-2xl mx-auto mt-8 text-base md:text-lg">
              Considered materials, heritage technique, and a refusal to rush. What you wear should carry the weight of intention — cut, finished and cared for as though it were meant to last a lifetime.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 6 — Client services & concierge */}
      <section className="max-w-[1700px] mx-auto px-5 md:px-10 py-20 md:py-28 border-b border-espresso/10" data-testid="pdp-services">
        <div className="grid md:grid-cols-3 gap-12 md:gap-16">
          {[
            { icon: Truck, title: "Delivery & Returns", body: "Complimentary express delivery and considered returns on every order.", to: "/pages/delivery" },
            { icon: Sparkles, title: "Private Styling", body: "Speak with us for personal guidance on fit, sizing and pairing.", to: "/pages/contact" },
            { icon: Scissors, title: "Care & Repair", body: "Guidance to keep each piece as it was the day it arrived.", to: "/pages/care-guide" },
          ].map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <s.icon strokeWidth={1.2} className="text-gold mb-5" size={26} />
              <h3 className="font-serif text-xl md:text-2xl tracking-tight mb-3">{s.title}</h3>
              <p className="text-espresso/65 font-light leading-relaxed text-sm md:text-base mb-4">{s.body}</p>
              <Link to={s.to} className="text-[0.66rem] tracking-[0.16em] uppercase hover-underline text-espresso/60">Learn more</Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 7 — You may also like */}
      <Rail overline="Complete the story" title="You may also like" items={related} testid="pdp-you-may-like" />

      {/* 8a — The full look (rest of this collection) */}
      <Rail overline="Continue the story" title="The full look" items={collectionRest} testid="pdp-full-look" />

      {/* 8b — Department atelier rail */}
      <Rail overline={collectionName ? `From ${collectionName}` : "Explore"} title={deptLabel} items={deptRail} testid="pdp-dept-rail" />

      {/* 9 — Closing brand signature */}
      <section className="bg-ivory py-28 md:py-44 border-t border-espresso/10" data-testid="pdp-signature">
        <Reveal className="text-center px-5">
          <div className="w-16 h-px bg-gold mx-auto mb-8" />
          <p className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-[0.06em] text-espresso">NOIRBYAMARA</p>
          <p className="text-[0.62rem] tracking-[0.3em] uppercase text-espresso/45 mt-6">Crafted in silence, worn in splendour</p>
        </Reveal>
      </section>

      <AnimatePresence>
        {lightbox !== null && galleryImgs.length > 0 && (
          <Lightbox images={galleryImgs} index={lightbox} onClose={() => setLightbox(null)}
            onNav={(d) => setLightbox((v) => (v + d + galleryImgs.length) % galleryImgs.length)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductPage;
